/**
 * Folders Service
 * Provides folder management for persistent file organization
 *
 * Folders are project-scoped. When using OAuth auth, the projectId must be provided.
 * When using API key auth, the project is inferred from the key.
 */

import type { GatewayClient } from './client'
import type {
  FolderResponse,
  FolderListResponse,
  CreateFolderRequest,
  UpdateFolderRequest,
  DeleteFolderRecursiveResponse,
  ListFoldersOptions,
} from './types'

// ============================================================================
// Types
// ============================================================================

/**
 * Options for creating a project-scoped folders service
 */
export interface ProjectFoldersOptions {
  /** Project ID for scoping folder operations (required for OAuth auth) */
  projectId: string
}

export interface FoldersService {
  /**
   * List folders
   */
  list(options?: ListFoldersOptions): Promise<FolderListResponse>

  /**
   * Create a new folder
   */
  create(request: CreateFolderRequest): Promise<FolderResponse>

  /**
   * Get a folder by ID
   */
  get(folderId: string): Promise<FolderResponse>

  /**
   * Update a folder (rename or move)
   */
  update(
    folderId: string,
    request: UpdateFolderRequest
  ): Promise<FolderResponse>

  /**
   * Delete an empty folder
   */
  delete(folderId: string): Promise<void>

  /**
   * Delete a folder and all its contents recursively
   */
  deleteRecursive(folderId: string): Promise<DeleteFolderRecursiveResponse>
}

// ============================================================================
// Implementation
// ============================================================================

/**
 * Create a FoldersService instance for a specific project.
 *
 * The projectId is required for OAuth authentication to specify which project's
 * folders to access. For API key authentication, the project is inferred from
 * the key, but the projectId query parameter is still sent for consistency.
 *
 * @param client - The GatewayClient instance
 * @param options - Options including the projectId
 */
export function createFoldersService(
  client: GatewayClient,
  options: ProjectFoldersOptions
): FoldersService {
  const { projectId } = options

  // Helper to build request options with projectId query parameter
  const withProjectParam = (
    additionalOptions?: Record<string, unknown>
  ): Record<string, unknown> => ({
    ...additionalOptions,
    params: {
      projectId,
      ...(additionalOptions?.params as Record<string, string>),
    },
  })

  return {
    async list(options?: ListFoldersOptions): Promise<FolderListResponse> {
      return client.get<FolderListResponse>(
        '/v1/folders',
        withProjectParam({
          params: options as Record<
            string,
            string | number | boolean | undefined
          >,
        })
      )
    },

    async create(request: CreateFolderRequest): Promise<FolderResponse> {
      return client.post<FolderResponse>(
        '/v1/folders',
        request,
        withProjectParam()
      )
    },

    async get(folderId: string): Promise<FolderResponse> {
      return client.get<FolderResponse>(
        `/v1/folders/${folderId}`,
        withProjectParam()
      )
    },

    async update(
      folderId: string,
      request: UpdateFolderRequest
    ): Promise<FolderResponse> {
      return client.patch<FolderResponse>(
        `/v1/folders/${folderId}`,
        request,
        withProjectParam()
      )
    },

    async delete(folderId: string): Promise<void> {
      await client.delete(`/v1/folders/${folderId}`, withProjectParam())
    },

    async deleteRecursive(
      folderId: string
    ): Promise<DeleteFolderRecursiveResponse> {
      return client.delete<DeleteFolderRecursiveResponse>(
        `/v1/folders/${folderId}`,
        withProjectParam({ params: { recursive: 'true' } })
      )
    },
  }
}
